import { Link, Text } from "@zapier/design-system";
import { useCurrentAccountId } from "@zapier/identity";

import BackButton from "./BackButton";
import { useAccounts } from "./useAccounts";
import { AccountLike } from "./types";
import AccountItem from "./AccountItem";
import styles from "./AccountSwitcher.module.css";

interface Props {
  currentAccountId: number;
  accounts: AccountLike[];
}

export const AccountSwitcher = ({ accounts, currentAccountId }: Props) => {
  return (
    <div className={styles.layoutCss}>
      <div className={styles.topCss}>
        <BackButton href="/app/canvas" />
      </div>
      <div className={styles.containerCss}>
        <div className={styles.contentCss}>
          <Text tag="h1" type="SectionHeader">
            Unable to to access Canvas
          </Text>
          <Text margin="20px 0" tag="p" type="Body1">
            Please try reloading the page or switching to a different account.
          </Text>
          <Text tag="h2" type="Body4">
            Switch accounts
          </Text>
          <div className={styles.accountsContainerCss}>
            {accounts.map((account) => (
              <AccountItem
                key={account.id}
                account={account}
                isCurrentAccount={currentAccountId === account.id}
              />
            ))}
          </div>
          <div>
            <Text margin="20px 0" tag="p" textAlign="center" type="Body1">
              Need help? <Link href="/app/get-help">Contact support</Link>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountSwitcherWithLoader = () => {
  const currentAccountId = useCurrentAccountId();
  const accounts = useAccounts();
  if (!currentAccountId) {
    return null;
  }
  const sortedAccounts = getSortedAccounts(accounts, currentAccountId);

  return (
    <AccountSwitcher
      accounts={sortedAccounts}
      currentAccountId={currentAccountId}
    />
  );
};

function getSortedAccounts(
  accounts: Array<AccountLike>,
  currentAccountId: number,
) {
  const sortAccounts = (a: AccountLike, b: AccountLike) => {
    if (a.id === currentAccountId) {
      return -1;
    }

    if (b.id === currentAccountId) {
      return 1;
    }

    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA > nameB) {
      return 1;
    }

    if (nameA < nameB) {
      return -1;
    }

    return 0;
  };

  return [...accounts].sort(sortAccounts);
}

export default AccountSwitcherWithLoader;
